﻿import React, { ReactNode } from 'react';

import type {
  TFinancialConditionCertificateResponse,
  TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type {
  TIncomeStructureByKvedCertificateResponse,
  TIncomeStructureByKvedFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';

export enum SortDirection {
  None = 'none',
  Asc = 'asc',
  Desc = 'desc',
}

export enum StatusFilterValue {
  All = 'ALL',
  New = 'NEW',
  Sent = 'SENT',
  Accepted = 'ACCEPTED',
  Denied = 'DENIED',
  PartlyAccepted = 'PARTLY_ACCEPTED',
}

export type TTokenExchangeRequest = {
  emailToken: string;
};

export type TTokenExchangeResponse = {
  accessToken: string;
};

export type TPageConfiguration = {
  page: number;
  size: number;
  totalElements: number;
};

export type TApiContragent = {
  id: number;
  code: string;
  displayName: string;
};

export type TApiSupervisorContragentBlock = {
  id: number;
  email: string;
  createdAt: string;
};

export type TApiSupervisorContragentBlocksResponse = {
  pageConfiguration: TPageConfiguration;
  blocks: TApiSupervisorContragentBlock[];
};

export type TRequireDocumentStatus = 'IN_PROGRESS' | 'PENDING' | 'ACCEPTED';

export type TApiRequiredDocument = {
  id: string;
  displayName: string;
  childPeriod: string;
  yearPeriod: string;
  deadline: string;
  status: TRequireDocumentStatus;
};

export type TApiRequiredDocumentsResponse = {
  pageConfiguration: TPageConfiguration;
  count: number;
  requiredDocuments: TApiRequiredDocument[];
};

export type TDocumentStatus = 'NEW' | 'SENT' | 'ACCEPTED' | 'DENIED';

export type TLocalFileEntry = {
  file?: File;
  signature?: File;
  isExternal?: boolean;
};

export type TLocalFiles = Record<string, TLocalFileEntry>;

export type TUseLocalFilesResult = {
  localFiles: TLocalFiles;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  sigFileInputRef: React.RefObject<HTMLInputElement | null>;
  hasFiles: boolean;
  handleUploadForDoc: (rowKey: string) => void;
  handleAddDocumentRow: (document: TApiApplicationDocument) => void;
  handleSaveFinancialCondition: (values: TFinancialConditionFormValues) => Promise<boolean>;
  handleLoadFinancialCondition: (
    document: TApiApplicationDocument
  ) => Promise<TFinancialConditionCertificateResponse | null>;
  handleSaveIncomeStructureByKved: (
    document: TApiApplicationDocument,
    values: TIncomeStructureByKvedFormValues
  ) => Promise<boolean>;
  handleLoadIncomeStructureByKved: (
    document: TApiApplicationDocument
  ) => Promise<TIncomeStructureByKvedCertificateResponse | null>;
  handleGenerateDocument: (document: TApiApplicationDocument, isFop?: boolean) => Promise<boolean>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleUploadSignatureClick: (rowKey: string, doc?: TApiApplicationDocument) => void;
  handleSignatureFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<boolean>;
  handleDeleteSignature: (rowKey: string, doc?: TApiApplicationDocument) => Promise<void>;
  handleDeleteDocument: (rowKey: string) => Promise<void>;
  signatureError: string | null;
  setSignatureError: React.Dispatch<React.SetStateAction<string | null>>;
  setSigningDocId: React.Dispatch<React.SetStateAction<string | null>>;
  setTargetDocId: React.Dispatch<React.SetStateAction<string | null>>;
};

export type TUseLocalFilesParams = {
  contragentId: number;
  applicationId: string;
  applicationVersion: number;
  applicationDocuments: TApiApplicationDocument[];
  documentRows: TApiApplicationDocument[];
  onDocumentsRefresh?: () => Promise<void>;
  onVirtualRowConsumed?: (rowKey: string) => void;
  onVersionMismatch?: () => void;
  onDocumentUploadStart?: () => void;
  onDocumentUploadQueued?: () => void;
  onDocumentUploadError?: () => void;
  onSignatureActionStart?: () => void;
  onSignatureActionQueued?: () => void;
  onSignatureActionError?: () => void;
  onSignatureUploadSuccess?: (doc: TApiApplicationDocument) => void;
  allowedUploadFileExtensions?: string[];
  onUnsupportedFileExtension?: () => void;
};

export type TCovenantsDocumentStableKeySource = {
  documentId: string | number;
  yearPeriod: string;
  childPeriod: string;
  deadline?: string;
};

export type TCovenantsDocumentRowIdentitySource = TCovenantsDocumentStableKeySource & {
  id?: string;
  fileName?: string;
  createdAt?: string;
};

export type TApiApplicationDocumentFile = {
  id?: string;
  fileId?: string | number;
  fileName?: string;
  comment?: string;
  createdAt?: string;
  status?: TApplicationStatus;
  isCustomSignFile?: boolean;
  hasSignedFile?: boolean;
  signedByName?: string;
  signetByCode?: string;
};

export type TApiApplicationDocumentFilePayload = TApiApplicationDocumentFile & {
  id: string;
};

export type TApiApplicationDocumentPayload = {
  version?: number;
  id?: string;
  documentName: string;
  documentId: string;
  fileName?: string;
  comment?: string;
  createdAt?: string;
  deadline?: string;
  status?: TApplicationStatus;
  isCustomSignFile?: boolean;
  hasSignedFile?: boolean;
  requiredDocumentStatus?: TRequireDocumentStatus;
  yearPeriod: string;
  childPeriod: string;
  signedByName?: string;
  signetByCode?: string;
  files?: TApiApplicationDocumentFilePayload[];
};

export type TApiApplicationDocument = {
  version: number;
  id: string | undefined;
  fileId?: string | number;
  rowId?: string;
  isVirtualRow?: boolean;
  documentName: string;
  documentId: string;
  fileName?: string;
  comment?: string;
  createdAt?: string;
  deadline?: string;
  status: TApplicationStatus;
  isCustomSignFile: boolean;
  hasSignedFile?: boolean;
  requiredDocumentStatus?: TRequireDocumentStatus;
  yearPeriod: string;
  childPeriod: string;
  signedByName?: string;
  signetByCode?: string;
};

export type TApiApplicationDocumentsResponse = {
  pageConfiguration: TPageConfiguration;
  count: number;
  applicationDocuments: TApiApplicationDocument[];
};

export type TApiApplicationUnsignedDocumentsResponse = {
  unsinged?: number;
};

export type TApiCovenant = {
  id: string;
  name: string;
  contactName: string;
};

export type TApiCovenantsResponse = {
  pageConfiguration: TPageConfiguration;
  count: number;
  covenants: TApiCovenant[];
};

export type TApiReadyCovenant = {
  status: 'CAN_BE_CLOSED' | 'WAITING_FOR_DOCS';
  shortName: string;
  longName: string;
  dealNumber: string;
  grafId: number;
  targetStatus: string;
  documentIdsGroup: number[];
  yearPeriod: string;
  childPeriod: string;
  deadline: string;
};

export type TApiReadyCovenantsResponse = {
  pageConfiguration: TPageConfiguration;
  count: number;
  covenants: TApiReadyCovenant[];
};

export type TApiReadyCovenantDocument = {
  id: number;
  displayName: string;
  yearPeriod: string;
  childPeriod: string;
  deadline: string;
  status: string;
};

export type TApiReadyCovenantDocumentsResponse = {
  documents: TApiReadyCovenantDocument[];
};

export type TApplicationStatus = Exclude<StatusFilterValue, StatusFilterValue.All>;

export type TApiApplication = {
  id: string;
  localId?: number;
  displayName: string;
  createdAt?: string;
  status: TApplicationStatus;
  deadline: string;
  range: string;
  version: number;
};

export type TApiApplicationsResponse = {
  pageConfiguration: TPageConfiguration;
  count: number;
  applications: TApiApplication[];
};

export type TApiSupervisorApplication = {
  id: string;
  localId?: number;
  segmentId?: string | number;
  segmentName?: string;
  contragentId?: string | number;
  contragentCode: string;
  contragentShortName: string;
  contragentFullName: string;
  createdAt: string;
  sentAt?: string;
  status: TApplicationStatus;
  acceptedBy: string;
  reviewedAt?: string;
  reviewedBy?: string;
  savedAt?: string;
  assignee: string;
  assigneAt?: string;
  version: number;
};

export type TApiSupervisorApplicationDocument = {
  id?: string;
  documentName: string;
  documentId: string | number;
  fileName?: string;
  comment?: string;
  createdAt?: string;
  deadline?: string;
  status?: TApplicationStatus;
  requiredDocumentStatus: string;
  hasSignedFile?: boolean;
  signedByName?: string;
  signetByCode?: string;
  yearPeriod: string;
  childPeriod: string;
  version: number;
  files?: TApiApplicationDocumentFilePayload[];
};

export type TApiSupervisorApplicationsResponse = {
  pageConfiguration: TPageConfiguration;
  count: number;
  supervisorApplications: TApiSupervisorApplication[];
};

export type TApiSupervisorApplicationDocumentsResponse = {
  applicationId?: string;
  pageConfiguration: TPageConfiguration;
  count: number;
  documents: TApiSupervisorApplicationDocument[];
};

export type TApiSegment = {
  id: string;
  displayName: string;
};

export type TApiSegmentSupervisor = {
  id: string;
  email: string;
};

export type TApiSupervisorSegment = {
  segment: TApiSegment;
  supervisors: TApiSegmentSupervisor[];
};

export type TApiSupervisorInfo = {
  hasApplicationAccess: boolean;
  hasAdministrationAccess: boolean;
  hasCallCenterOperatorAccess: boolean;
  hasBranchManagerAccess: boolean;
  hasClientViewAccess: boolean;
  hasEmailBlackListAccess: boolean;
};

export type TApiApplicationDetails = {
  id: string;
  localId?: number;
  createdAt: string;
  deadline?: string;
  status: TApplicationStatus;
  version: number;
};

export type TApiApplicationVersionResponse = {
  version: number;
};

export type TApiUploadApplicationDocumentResponse = TApiApplicationVersionResponse & {
  id: string;
};

export type TApiApplicationCreated = {
  id: string;
};

export type TApiSubmitApplicationResponse = {
  documentsWithInvalidSignatures: string[];
  documentsWithRNOCPPMismatch: string[];
  generatedProtocols: string[];
  noKepDocuments: string[];
};

export type TSignatureValidationDocumentState = {
  hasInvalidSignature: boolean;
  hasRNOCPPMismatch: boolean;
  hasNoKep: boolean;
  hasGeneratedProtocol: boolean;
};

export type TSignatureValidationState = Record<string, TSignatureValidationDocumentState>;

export type TCreateApplicationRequest = {
  comment?: string;
};
export type TColumnDef<T> = {
  key: string;
  header: string;
  align?: 'left' | 'center' | 'right';
  render: (row: T) => ReactNode;
  headerComponent?: ReactNode;
};

export type TUseCovenantsApplicationRefreshParams = {
  applicationId?: string;
  application: TApiApplicationDetails | null;
  contragentId?: number;
  documentsPage: number;
  documentsRowsPerPage: number;
};

export type TRefreshDocumentsParams = {
  applicationId: string;
  status: TApplicationStatus;
};

export type TRequiredUploadTone = 'solid' | 'outlined';

export type TRequiredUploadConfig = {
  requiresConfirmation: boolean;
  tone: TRequiredUploadTone;
};
